'use client';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  TypeMediaCollectionPlayerFields,
  TypeCourseSubChapter,
  TypeMediaCollectionChapterFields,
} from '@/core/generated-types';
import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';
import {
  Errors,
  HeaderText,
  Loader,
  MediaCollectionChapter,
  MediaCollectionSupportingContent,
  MediaCollectionVideo,
  RichText,
  Tabs,
} from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ExpandedTabDisplayTypes,
  NavigationDisplayTypes,
} from '@/components/common/Tabs/Tabs';
import { TypeMediaCollection } from '@/lib/api/playbackService/types';
import useMediaCollectionMediaAssets from '@/lib/api/playbackService/hooks/useMediaCollectionMediaAssets';
import useMediaCollectionPlaybackHistories from '@/lib/api/playbackService/hooks/useMediaCollectionPlaybackHistories';
import useTokenSetVault from '@/lib/api/playbackService/hooks/useTokenSetVault';
import { calculatePercentage } from '@/utils/calculations';
import {
  TypeSignedMuxVideoAsset,
  TypeSignedMuxVideoUpload,
} from '../MediaCollectionVideo/utils';
import useMediaCollectionStreamingQueue, {
  TypeVideoPosition,
} from './useMediaCollectionStreamingQueue';
import useMediaCollectionNavigation from './useMediaCollectionNavigation';
import { MediaCollectionPlayerPreview } from '@/components/mediaCollection/MediaCollectionPlayer/MediaCollectionPlayerPreview';
import {
  _findPlaybackHistory,
  _findVideo,
  _getInitialVideoId,
  MediaCollectionPlayerProps,
  MediaCollectionSubChapterProgressBar,
} from './utils';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import interpolate from '@/utils/translations/interpolate';
import {
  capturePlaybackProgressEvent,
  PLAYBACK_EVENT_TYPES,
} from '@/utils/dataLayer';
import { PLAYER_PLACEHOLDER_CONTAINER_STYLES } from '../MediaCollectionVideo/MediaCollectionVideo';

const MediaCollectionPlayer: FC<MediaCollectionPlayerProps> = ({
  mediaCollectionProduct,
  videos,
  songs,
  playbackHistoryMutations,
  playbackHistories,
  additionalSupport,
  additionalSupportSectionTitle,
  initialVideoId,
}) => {
  const mediaCollectionNavigation = useMediaCollectionNavigation(
    mediaCollectionProduct,
  );
  const {
    activeVideoPosition,
    onDeckVideoPosition,
    activeSubChapter,
    onDeckSubChapter,
    regenerateStreamingQueueFromVideo,
    reportActiveVideoCompleted,
  } = useMediaCollectionStreamingQueue(mediaCollectionProduct, initialVideoId);

  const {
    retrieveItemFromVault: retrieveVideoAssetFromVault,
    fetchTokenSetForItem: fetchTokenSetForVideoAsset,
  } = useTokenSetVault(videos.map((video) => video.playback_id));
  const {
    retrieveItemFromVault: retrieveSongAssetFromVault,
    fetchTokenSetForItem: fetchTokenSetForSongAsset,
  } = useTokenSetVault(songs?.map((song) => song.playback_id));

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );

  const activeVideo = _findVideo(videos, activeSubChapter);
  const onDeckVideo = _findVideo(videos, onDeckSubChapter);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const activeTokenSet =
    activeVideo && retrieveVideoAssetFromVault(activeVideo.playback_id);
  const activePlaybackHistory = _findPlaybackHistory(
    playbackHistories,
    activeVideo,
  );

  // handle fetching token sets as we progress through the queue of videos
  useEffect(() => {
    if (activeVideo) {
      fetchTokenSetForVideoAsset(activeVideo.playback_id, false);
    }

    if (onDeckVideo) {
      fetchTokenSetForVideoAsset(onDeckVideo.playback_id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideo, onDeckVideo]);

  useEffect(() => {
    function ensureStreamingVideoIsVisibleInNavigation(
      activeVideoPosition: TypeVideoPosition,
    ) {
      const { partId, chapterId } =
        mediaCollectionNavigation.navigationPosition;
      const activeVideoExistsInDifferentPart =
        partId !== activeVideoPosition.partId;
      const activeVideoExistsInDifferentChapter =
        chapterId !== activeVideoPosition.chapterId;

      if (
        activeVideoExistsInDifferentPart ||
        activeVideoExistsInDifferentChapter
      ) {
        mediaCollectionNavigation.navigateToPosition(
          activeVideoPosition.partId,
          // Chapter is collapsed by default
        );
      }
    }

    if (activeVideoPosition) {
      ensureStreamingVideoIsVisibleInNavigation(activeVideoPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideoPosition]);

  const [enableAutoplay, setEnableAutoplay] = useState(false);

  function onVideoComplete() {
    if (activePlaybackHistory) {
      const muxVideoUpload = activeSubChapter?.fields.video.fields
        .muxVideoUpload as TypeSignedMuxVideoUpload;
      playbackHistoryMutations.update(
        activePlaybackHistory.id,
        muxVideoUpload.duration,
        new Date(),
      );
    }

    if (onDeckVideoPosition) {
      // if the on deck video begins a new part, navigate there
      if (onDeckVideoPosition.partId !== activeVideoPosition.partId) {
        mediaCollectionNavigation.navigateToPosition(
          onDeckVideoPosition.partId,
        );
      }

      // if the on deck video begins a new chapter, navigate there
      if (onDeckVideoPosition.chapterId !== activeVideoPosition.chapterId) {
        mediaCollectionNavigation.navigateToChapter(
          onDeckVideoPosition.chapterId,
        );
      }
    }

    reportActiveVideoCompleted();
  }

  function onVideoProgress(timeInSeconds: number) {
    if (!activeVideo) return;

    const formattedSeconds = Math.trunc(timeInSeconds);
    const isVideoPaused = formattedSeconds === activePlaybackHistory?.head;

    if (isVideoPaused) {
      return;
    }

    const muxVideoUpload: TypeSignedMuxVideoUpload =
      activeSubChapter?.fields.video.fields.muxVideoUpload;
    const { duration } = muxVideoUpload || {};
    const roundedDuration = Math.trunc(duration);
    const percentageComplete = calculatePercentage(
      formattedSeconds,
      roundedDuration,
      'floor',
    );

    capturePlaybackProgressEvent(PLAYBACK_EVENT_TYPES.PROGRESS, {
      title: activeSubChapter?.fields.title || '',
      percentComplete: percentageComplete,
      head: formattedSeconds,
    });

    if (activePlaybackHistory) {
      playbackHistoryMutations.update(
        activePlaybackHistory.id,
        timeInSeconds,
        undefined,
      );
    } else {
      playbackHistoryMutations.create(activeVideo.id);
    }
    if (!enableAutoplay) {
      setEnableAutoplay(true);
    }
  }

  const tabs = useMemo(() => {
    function isVideoActive(videoSysId: string) {
      return activeSubChapter?.sys.id === videoSysId;
    }

    function streamVideoOnDemand(videoSysId: string) {
      regenerateStreamingQueueFromVideo(videoSysId);
    }

    function getPlaybackHistoryForSubChapter(subChapter: TypeCourseSubChapter) {
      const video = _findVideo(videos, subChapter);
      return _findPlaybackHistory(playbackHistories, video);
    }

    function createProgressBar(
      subChapter: TypeCourseSubChapter,
    ): MediaCollectionSubChapterProgressBar {
      const playbackHistory = getPlaybackHistoryForSubChapter(subChapter);
      const { duration } = subChapter.fields.video.fields.muxVideoUpload;
      const roundedDuration = Math.trunc(duration);
      const { head } = playbackHistory || {};
      const percentageComplete = calculatePercentage(
        head,
        roundedDuration,
        'floor',
      );
      const hasEverBeenCompleted = !!playbackHistory?.completed_at;

      return {
        subChapterId: subChapter.sys.id,
        percentageComplete,
        isComplete: hasEverBeenCompleted,
      };
    }

    function getSubChapterProgressBars(
      mediaCollectionChapter: Entry<TypeMediaCollectionChapterFields>,
    ): MediaCollectionSubChapterProgressBar[] {
      return (mediaCollectionChapter.fields.subChapters || []).map(
        createProgressBar,
      );
    }

    return mediaCollectionProduct.fields.parts.map(
      (mediaCollectionPart, i) => ({
        heading: mediaCollectionPart.fields.title,
        title: interpolate(mediaCollectionPart.fields.titleWithSectionPrefix, {
          currentSection: i + 1,
          sectionCount: mediaCollectionProduct.fields.parts.length,
        }),
        onSelectTab: () =>
          mediaCollectionNavigation.navigateToPosition(
            mediaCollectionPart.sys.id,
          ),
        jsxContent: (
          <>
            {mediaCollectionPart.fields.chapters.map((chapter, i) => (
              <MediaCollectionChapter
                key={chapter.sys.id}
                chapter={chapter}
                isFirstChapter={i === 0}
                isActiveChapter={
                  chapter.sys.id ===
                  mediaCollectionNavigation.navigationPosition.chapterId
                }
                isActiveVideoInChapter={(chapter.fields.subChapters || []).some(
                  (subChapter) =>
                    subChapter.sys.id === activeSubChapter?.sys.id,
                )}
                isVideoActive={isVideoActive}
                streamVideoOnDemand={streamVideoOnDemand}
                navigateToChapter={() =>
                  mediaCollectionNavigation.navigateToChapter(chapter.sys.id)
                }
                closeChapter={() =>
                  mediaCollectionNavigation.navigateToChapter('')
                }
                progressBars={getSubChapterProgressBars(chapter)}
                isVideoPlaying={isVideoPlaying}
                setIsVideoPlaying={setIsVideoPlaying}
              />
            ))}
          </>
        ),
      }),
    );
  }, [
    activeSubChapter?.sys.id,
    mediaCollectionNavigation,
    mediaCollectionProduct.fields.parts,
    playbackHistories,
    regenerateStreamingQueueFromVideo,
    videos,
    isVideoPlaying,
  ]);

  // Used when a single tab is created to wrap all content
  const activeTab = tabs[mediaCollectionNavigation.activePartIndex];

  // it is expected that the Media Collection player should always have a video loaded in the player
  if (!activeSubChapter) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  return (
    <div data-testid="media-collection-player-container">
      <div className="lg:grid lg:grid-cols-12 lg:gap-4">
        <div className="sticky top-[72px] z-10 lg:z-0 lg:col-span-7">
          <MediaCollectionVideo
            muxVideo={
              activeSubChapter.fields.video.fields as TypeSignedMuxVideoAsset
            }
            title={activeSubChapter.fields.title}
            seriesTitle={mediaCollectionProduct.fields.title}
            playbackServiceVideoId={activeVideo?.id}
            onVideoComplete={onVideoComplete}
            onVideoProgress={onVideoProgress}
            refetchTokens={fetchTokenSetForVideoAsset}
            startTime={activePlaybackHistory?.head}
            userUuid={activePlaybackHistory?.user_uuid}
            tokenSet={activeTokenSet}
            isSigned
            shouldAutoplay={enableAutoplay}
            setIsVideoPlaying={setIsVideoPlaying}
          />
        </div>
        <div
          className="mb-8 mt-4 lg:col-span-5 lg:mx-0 lg:mb-0 lg:mt-0 lg:flex lg:flex-col"
          data-testid="media-collection-player-navigation-container"
        >
          <div className="lg:relative lg:h-full">
            <div className="lg:absolute lg:left-0 lg:top-0 lg:flex lg:h-full lg:w-full lg:flex-col lg:overflow-y-scroll lg:overscroll-contain">
              {tabs.length === 1 ? (
                <>
                  <div className="mx-6 lg:mx-0 pb-4 lg:pb-0 border-b border-charcoal/20 lg:border-b-0">
                    <HeaderText size={HeaderSize.H2} text={activeTab.heading} />
                  </div>
                  {activeTab.jsxContent}
                </>
              ) : (
                <Tabs
                  tabs={tabs}
                  shouldUseActiveTabIndexOverride
                  activeTabIndexOverride={
                    mediaCollectionNavigation.activePartIndex
                  }
                  setActiveTabIndexOverride={(idx) =>
                    mediaCollectionNavigation.navigateToPosition(
                      mediaCollectionProduct.fields.parts[idx].sys.id,
                    )
                  }
                  expandedTabDisplayType={ExpandedTabDisplayTypes.JSX}
                  navigationDisplayType={NavigationDisplayTypes.Single}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {additionalSupport && (
        <BackgroundColorWrapper color="aqua-tint">
          <div
            className="container mx-auto py-20 lg:max-w-[1128px]"
            data-testid="media-collection-player-additional-support-container"
          >
            <HeaderText
              text={additionalSupportSectionTitle}
              size={HeaderSize.H2}
              align="center"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {additionalSupport.map((additionalSupportItem) => (
                <MediaCollectionSupportingContent
                  key={additionalSupportItem.sys.id}
                  {...additionalSupportItem.fields}
                  fetchTokenSetForSongAsset={fetchTokenSetForSongAsset}
                  retrieveSongAssetFromVault={retrieveSongAssetFromVault}
                />
              ))}
            </div>
            {mediaCollectionProduct.fields.disclaimerCopy && (
              <div className="container pt-20 sm:px-4 lg:mr-auto lg:max-w-[750px]">
                <RichText
                  content={
                    mediaCollectionProduct.fields.disclaimerCopy.fields
                      .richText as Document
                  }
                />
              </div>
            )}
          </div>
        </BackgroundColorWrapper>
      )}
    </div>
  );
};

export const MediaCollectionPlayerWithData: FC<
  TypeMediaCollectionPlayerFields
> = (fields) => {
  const { mediaCollectionProduct } = fields;
  const { playgeniusProductAnchor } = mediaCollectionProduct?.fields;
  const {
    data: mediaAssets,
    loading: mediaAssetsLoading,
    error: mediaAssetsError,
  } = useMediaCollectionMediaAssets(
    playgeniusProductAnchor,
    TypeMediaCollection.SKILL_SET,
  );
  const {
    data: playbackHistoriesData,
    loading: playbackHistoriesLoading,
    error: playbackHistoriesError,
    mutations: playbackHistoryMutations,
  } = useMediaCollectionPlaybackHistories(
    playgeniusProductAnchor,
    TypeMediaCollection.SKILL_SET,
  );
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );

  if (mediaAssetsLoading || playbackHistoriesLoading) {
    return (
      <div className={PLAYER_PLACEHOLDER_CONTAINER_STYLES}>
        <Loader loading />
      </div>
    );
  }

  if (mediaAssetsError || playbackHistoriesError) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  const videos = mediaAssets?.media_assets?.filter(
    (media_asset) => media_asset.type === 'Video',
  );

  const songs = mediaAssets?.media_assets?.filter(
    (media_asset) => media_asset.type === 'Song',
  );

  if (!videos || videos.length === 0) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  const initialVideoId = _getInitialVideoId(
    videos,
    playbackHistoriesData?.playback_histories,
  );

  return (
    <MediaCollectionPlayer
      {...fields}
      videos={videos}
      songs={songs}
      playbackHistories={playbackHistoriesData?.playback_histories}
      playbackHistoryMutations={playbackHistoryMutations}
      initialVideoId={initialVideoId}
    />
  );
};

export const MediaCollectionPlayerTemplateAssignment: FC<
  TypeMediaCollectionPlayerFields
> = (props) => {
  const { isPreviewPlayer } = props;

  if (isPreviewPlayer) {
    return <MediaCollectionPlayerPreview {...props} />;
  }

  return <MediaCollectionPlayerWithData {...props} />;
};

export default MediaCollectionPlayerTemplateAssignment;
